/* eslint-disable */
import * as React from 'react';
import { Suspense, lazy } from 'react';
import { inject, observer } from 'mobx-react';
import AppStore, { ContentType } from './appStore';
import './appContainerStyles.scss';
import AppTopNav from './topNav/appTopNav';
import { CookieStore } from '../../cookiesStore/cookieStore';
import LoadingSpinner from 'components/loadingSpinner/loadingSpinner';
import { action, observable } from 'mobx';
import DocSplitPanel from 'pages/document/components/splitPanel/docSplitPanel';
import AppLayout from '@amzn/awsui-components-react/polaris/app-layout';
import Alert from '@amzn/awsui-components-react/polaris/alert';
import { getChatbotEnabled } from 'api/graphql/query';
import { PolicyAssistantFab } from '../../components/genAI/policyAssistant/PolicyAssistantFab';
import { agenticSurfaceStore } from '../../components/genAI/policyAssistant/agenticSurfaceStore';

const GRCAIAssistant = lazy(() => import('@amzn/grc-ai-assistant-frontend'));
const Breadcrumb = lazy(() => import('./breadcrumb/breadcrumb'));
const Feedback = lazy(() => import('../../components/feedback/feedback'));
const DocSideNav = lazy(() => import('../../pages/document/components/sideNav/docSideNav'));

/**
 * TEMPORARY UAT toggle for the chat assistant.
 * true  → legacy GRCAIAssistant FAB (current default until the new experience is UAT'd)
 * false → new in-package Policy Assistant FAB
 * Remove this flag and the legacy branch once the new chat option passes UAT.
 */
const USE_LEGACY_CHAT_ASSISTANT = true;

interface Props {
  appStore?: AppStore;
  children: any;
}

@inject('appStore') @observer
export class AppContainer extends React.PureComponent<Props, unknown> {


  @observable spliPanelClose: Boolean = true;
  @observable chatbotEnabled: boolean = true; // Default to true to avoid flickering
  @observable showChatbotAlert: boolean = false;

  constructor(props: Props) {
    super(props);
    const { appStore } = this.props;
    appStore!.load();
  }

  /**
   * Handles actions for clicks on the AppLayout content.
   */
  handleAppLayoutClick(): void {
    const { appStore } = this.props;

    // Hide the feedback component for outside clicks.
    if (appStore?.isFeedbackVisible) appStore.showFeedback(false, true);
  }

  /**
   * Handles actions for clicks on the topnav.
   */
  handleTopNavClick(): void {
    const { appStore } = this.props;
    // Hide the feedback component for outside clicks.
    if (appStore?.isFeedbackVisible) appStore.showFeedback(false);
  }

  /**
   * Handles fetching chatbot enabled status
   */
  componentDidMount(): void {
    const { appStore } = this.props;
    
    appStore?.api.query(getChatbotEnabled, {}).then(action((enabledResult) => {
      // Process chatbot enabled status
      if (enabledResult && enabledResult.data && typeof enabledResult.data.chatbotEnabled === 'boolean') {
        this.chatbotEnabled = enabledResult.data.chatbotEnabled;
      }

      // Show alert if chatbot is disabled
      if (!this.chatbotEnabled) {
        this.showChatbotAlert = true;
        // Auto-hide alert after 10 seconds
        setTimeout(action(() => {
          this.showChatbotAlert = false;
        }), 10000);
      }
    })).catch((error) => {
      console.error("Error fetching chatbot data: ", error);
      // Default to enabled on error (fail-safe)
      this.chatbotEnabled = true;
    });
  }

  // See Fixed Top Navigation section:
  // https://polaris.a2z.com/components/awsui-top-navigation/?example=with-utility-controls&tabId=api
  render(): any {
    const { children, appStore } = this.props;
    const breadcrumbItems = appStore?.breadcrumbItems || [];
    const disableContentPaddings = (appStore?.useDocumentLayout && appStore?.contentType !== ContentType.WIZARD) || appStore?.contentType === ContentType.DASHBOARD;

    return (
      <div className="awsui">
        {/* <AnnotationContextLayer appStore={appStore} data={appStore?.tutorialData}> */}
        <div style={{ position: 'sticky', top: 0, zIndex: 1002 }} onClick={() => this.handleTopNavClick()}>
          <AppTopNav />
        </div>
        {this.showChatbotAlert && (
          <div style={{ 
            position: 'fixed', 
            top: '60px', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            zIndex: 1003, 
            maxWidth: '400px',
            width: '100%',
            padding: '0 20px'
          }}>
            <Alert
              type="info"
              dismissible
              onDismiss={action(() => this.showChatbotAlert = false)}
              header="Chatbot Temporarily Disabled"
            >
              The AI assistant chatbot is currently disabled. Please check back later.
            </Alert>
          </div>
        )}
        {appStore!.loading ? <LoadingSpinner size="large" absoluteCenter /> : (
          <AppLayout
            headerVariant="high-contrast"
            disableContentPaddings={disableContentPaddings}
            className="policy--app"
            contentType={appStore!.contentType}
            content={
              (appStore?.useDocumentLayout) || appStore!.contentType === ContentType.WIZARD ? (
                <>

                  {appStore!.useStickyBreadcrumbs && (
                    <div className={'custom-home__header-sticky'}>
                      <div style={{ paddingTop: '1rem' }}>
                        {breadcrumbItems?.length > 1 && (
                          <Suspense fallback={<></>}>
                            <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                          </Suspense>
                        )}
                      </div>
                    </div>
                  )}
                  {children}
                </>
              ) : (
                <section onClick={() => this.handleAppLayoutClick()} style={{ padding: appStore?.useDocumentLayout ? '1rem 4rem' : '0rem 0rem' }}>
                  {breadcrumbItems.length > 1 && appStore?.useDocumentLayout && (
                    <Suspense fallback={<></>}>
                      <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                    </Suspense>
                  )}
                  {children}
                </section>
              )
            }
            breadcrumbs={
              breadcrumbItems.length > 1 && !appStore?.useDocumentLayout && (
                <Suspense fallback={<></>}>
                  <Breadcrumb breadcrumbItems={breadcrumbItems} isLoading={appStore!.loading} />
                </Suspense>
              )
            }
            headerSelector="#policy--topnav"
            toolsHide={appStore?.toolsHide ?? true}
            toolsOpen={appStore?.toolsOpen ?? false}
            navigation={
              <Suspense fallback={<></>}>
                <DocSideNav
                  sideNavStore={appStore?.docSideNavStore}
                  navItems={appStore?.sideNavItems}
                  isLoading={appStore?.sideNavItems === 'LOADING'}
                />
              </Suspense>
            }
            navigationOpen={appStore?.isSideNavOpen ?? true}
            onNavigationChange={e => appStore?.handleIsSideNavOpen(e.detail.open)}
            tools={appStore?.toolBarContent}
            navigationHide={appStore?.hideSideNav || appStore?.sideNavItems.length === 0}
            onToolsChange={() => {
              appStore?.setToolsHide();
              appStore?.setToolsOpen();
            }}
            toolsWidth={appStore?.toolBarWidth}
            navigationWidth={300}
            splitPanel={<DocSplitPanel appStore={appStore} />}
            splitPanelOpen={appStore!.splitPanelOpen}
            splitPanelPreferences={{ position: 'side' }}
            onSplitPanelToggle={action(e => {
              const isOpen = e.detail.open;
        
              if (isOpen) {
                appStore!.setSplitPanel(true);
                appStore!.setSplitPanelOpen(true);
              } else {
                appStore!.setSplitPanelOpen(false);
              }
            })}
          />
        )}
        {appStore?.isFeedbackVisible && (
          <Suspense fallback={<></>}>
            <Feedback appStore={appStore} defaultOpen primary position="top-right" />
          </Suspense>
        )}
        <CookieStore ref={appStore?.cookiesStore} didMount={() => appStore?.checkCookiesStore()} />
        {/* </AnnotationContextLayer> */}
        {/* Chat assistant — legacy GRCAIAssistant by default; the new in-package Policy
            Assistant once the UAT toggle is flipped. See USE_LEGACY_CHAT_ASSISTANT.
            The legacy FAB keeps its server kill-switch (chatbotEnabled) and additionally
            hides on agentic surfaces so it never overlaps a dedicated agentic chat. The
            new FAB self-hides on agentic surfaces internally. */}
        {USE_LEGACY_CHAT_ASSISTANT ? (
          <div className='grc-assistant-wrapper'>
            {this.chatbotEnabled && !agenticSurfaceStore.isActive && (
              <Suspense fallback={<></>}>
                <GRCAIAssistant apps="policy" searchFolder={appStore?.getParentFolder} />
              </Suspense>
            )}
          </div>
        ) : (
          <PolicyAssistantFab
            userName={appStore?.currentUserID}
            getPageContext={() => {
              const crumbs = (appStore?.breadcrumbItems || [])
                .map((b: any) => b?.label)
                .filter((l: any): l is string => typeof l === 'string' && l.trim().length > 0);
              const doc = appStore?.docSideNavStore?.docPageStore?.document as any;
              return {
                breadcrumb: crumbs.join(' > ') || undefined,
                documentTitle: doc?.title,
                documentType: doc?.documentType,
                documentId: doc?.id || doc?.documentId,
              };
            }}
          />
        )}
      </div>
    );
  }
}

export default AppContainer;
